import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, RefreshCcw, Play, CheckCircle2, Sliders, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const BrandEssenceGame = ({ id = "game" }) => {
  const [gameState, setGameState] = useState("start"); // start, playing, success
  const [sliders, setSliders] = useState({ vibrancy: 50, complexity: 50, contrast: 50 });
  const [target, setTarget] = useState({ vibrancy: 80, complexity: 20, contrast: 60 });
  const [score, setScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);

  const initGame = () => {
    setTarget({
      vibrancy: Math.floor(Math.random() * 80) + 10,
      complexity: Math.floor(Math.random() * 80) + 10,
      contrast: Math.floor(Math.random() * 80) + 10
    });
    setSliders({ vibrancy: 50, complexity: 50, contrast: 50 });
    setGameState("playing");
  };

  const checkMatch = useCallback(() => {
    const diff = 
      Math.abs(sliders.vibrancy - target.vibrancy) +
      Math.abs(sliders.complexity - target.complexity) +
      Math.abs(sliders.contrast - target.contrast);
    
    if (diff < 15) {
      setGameState("success");
      setScore(s => s + 1);
    }
  }, [sliders, target]);

  useEffect(() => {
    if (gameState === "playing") {
      checkMatch();
    }
  }, [sliders, gameState, checkMatch]);

  const getPreviewStyle = (vals) => {
    const hue = (vals.vibrancy * 2) + 200; // Blue to Gold range
    const blur = (80 - vals.complexity) / 10;
    const opacity = vals.contrast / 100;
    
    return {
      background: `hsl(${hue}, 70%, 50%)`,
      filter: `blur(${blur}px) contrast(${vals.contrast}%)`,
      opacity: opacity,
      borderRadius: `${vals.complexity}%`,
      transform: `scale(${0.5 + vals.vibrancy / 100})`
    };
  };

  return (
    <section id={id} className="py-24 relative overflow-hidden bg-[#0a0a0a]">
      <div className="container-xl px-6">
        <div className="flex flex-col mb-12">
          <div className="text-left mb-8">
            <Kicker index="ID-01" label="Creative Workshop" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Brand <span className="italic gold-text">Essence</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              Fine-tune the visual variables to match the target brand identity. Precision is identity.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center bg-[var(--ink-2)] p-8 md:p-16 rounded-[40px] border border-[var(--rule)] relative shadow-2xl">
          
          {/* Visual Preview Area */}
          <div className="relative aspect-[4/5] md:aspect-square bg-black rounded-3xl overflow-hidden border border-[var(--rule)] flex items-center justify-center">
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
            
            {/* Target Indicator */}
            <div className="absolute top-6 left-6 flex items-center gap-3 z-20">
              <div className="w-3 h-3 rounded-full bg-[var(--gold)] animate-pulse" />
              <span className="text-[10px] uppercase tracking-widest text-[var(--gold)]">Target Identity</span>
            </div>

            {/* Visual Representation */}
            <div className="relative w-64 h-64 transition-all duration-500">
              {/* Target (Ghost) */}
              <div 
                className="absolute inset-0 opacity-10 border-2 border-dashed border-white rounded-full scale-110"
                style={{
                  ...getPreviewStyle(target),
                  border: '2px dashed rgba(255,255,255,0.3)',
                  background: 'transparent'
                }}
              />
              {/* Current */}
              <motion.div 
                animate={{ ...getPreviewStyle(sliders) }}
                className="w-full h-full shadow-[0_0_50px_rgba(201,169,97,0.2)]"
              />
            </div>

            {/* Matching HUD */}
            <div className="absolute bottom-6 inset-x-6 flex justify-between items-end">
              <div className="text-left">
                <div className="text-[10px] text-[var(--muted)] uppercase tracking-widest mb-1">ACCURACY</div>
                <div className="text-2xl font-impact text-white">
                  {Math.max(0, 100 - Math.floor((Math.abs(sliders.vibrancy - target.vibrancy) + Math.abs(sliders.complexity - target.complexity) + Math.abs(sliders.contrast - target.contrast)) / 3))}%
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-[var(--muted)] uppercase tracking-widest mb-1">IDENTITIES CRAFTED</div>
                <div className="text-2xl font-impact text-[var(--gold)]">{score}</div>
              </div>
            </div>
          </div>

          {/* Controls Area */}
          <div className="relative z-40">
            {gameState === "start" ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10 flex flex-col items-center justify-center min-h-[300px]"
              >
                <div className="mb-8 relative">
                  <div className="absolute inset-0 bg-[var(--gold)] blur-3xl opacity-20 animate-pulse" />
                  <Palette className="w-16 h-16 text-[var(--gold)] relative z-10 opacity-80" />
                </div>
                <h3 className="font-display text-4xl text-white mb-8 italic tracking-wide">Ready to Engineer?</h3>
                <button 
                  className="group relative px-12 py-5 rounded-full overflow-hidden transition-all duration-500 hover:scale-105 active:scale-95"
                  onClick={initGame}
                  data-cursor="Start Mission"
                >
                  {/* Glassmorphism Background */}
                  <div className="absolute inset-0 bg-white/5 backdrop-blur-md border border-white/20 group-hover:bg-white/10 group-hover:border-[var(--gold)]/50 transition-all duration-500" />
                  
                  {/* Animated Border/Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_30px_rgba(201,169,97,0.3)]" />
                  
                  <span className="relative z-10 flex items-center gap-3 text-[var(--gold)] font-impact text-xl tracking-[0.2em] uppercase">
                    Enter Workshop <Play size={18} className="fill-[var(--gold)]" />
                  </span>
                </button>
              </motion.div>
            ) : (
              <div className="space-y-12">
                <SliderControl 
                  label="Vibrancy" 
                  value={sliders.vibrancy} 
                  onChange={(v) => setSliders({...sliders, vibrancy: v})} 
                  target={target.vibrancy}
                />
                <SliderControl 
                  label="Complexity" 
                  value={sliders.complexity} 
                  onChange={(v) => setSliders({...sliders, complexity: v})} 
                  target={target.complexity}
                />
                <SliderControl 
                  label="Contrast" 
                  value={sliders.contrast} 
                  onChange={(v) => setSliders({...sliders, contrast: v})} 
                  target={target.contrast}
                />
                
                <div className="pt-6 border-t border-[var(--rule)]">
                  <p className="text-[11px] text-[var(--muted)] uppercase tracking-[0.2em] mb-6 leading-loose">
                    Adjust the parameters until the core essence aligns with the target phantom.
                  </p>
                  <button 
                    className="w-full py-4 border border-[var(--rule)] rounded-full text-[var(--cream-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-all uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-3"
                    onClick={initGame}
                  >
                    <RefreshCcw size={14} /> Randomize Target
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Success Overlay */}
          <AnimatePresence>
            {gameState === "success" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/90 backdrop-blur-xl z-50 rounded-[40px] flex flex-col items-center justify-center p-12 text-center"
              >
                <motion.div
                  initial={{ scale: 0.5, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  className="w-32 h-32 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)] flex items-center justify-center mb-8"
                >
                  <CheckCircle2 className="text-[var(--gold)] w-16 h-16" />
                </motion.div>
                <h3 className="font-display text-5xl text-white mb-4">IDENTITY <span className="italic gold-text">MATCHED</span></h3>
                <p className="text-[var(--cream-muted)] text-sm tracking-[0.3em] uppercase mb-12">Visual Harmony Achieved</p>
                <button 
                  className="btn-primary px-12" 
                  onClick={initGame}
                  data-cursor="Next Mission"
                >
                  Next Identity
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Start Screen Overlay (with thumbnail) */}
          {gameState === "start" && (
            <div className="absolute inset-0 z-30 rounded-[40px] overflow-hidden pointer-events-none">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-30"
                style={{ backgroundImage: "url('/images/brand_essence_thumb.png')" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] to-transparent" />
            </div>
          )}
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Brand Essence Workshop"
        instructions={[
          { title: "Identify the Essence", desc: "Look at the 'Target Identity' phantom in the visual area. This is the brand personality you need to capture." },
          { title: "Adjust the Sliders", desc: "Use the Vibrancy, Complexity, and Contrast sliders to modify your current visual representation." },
          { title: "Check the HUD", desc: "Keep an eye on the 'Accuracy' percentage. Your goal is to reach over 95% accuracy to successfully match the identity." },
          { title: "Craft the Legacy", desc: "Once you achieve harmony, the identity is 'Matched' and added to your crafted collection. Try to craft as many as possible!" }
        ]}
      />
    </section>
  );
};

const SliderControl = ({ label, value, onChange, target }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-end">
      <label className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-impact">{label}</label>
      <span className="text-xl font-display text-white">{value}</span>
    </div>
    <div className="relative h-12 flex items-center">
      {/* Target Marker */}
      <div 
        className="absolute w-px h-6 bg-white/20 z-0" 
        style={{ left: `${target}%` }}
      />
      <input 
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-1 bg-[var(--rule)] appearance-none cursor-pointer accent-[var(--gold)] rounded-full outline-none"
      />
    </div>
  </div>
);

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default BrandEssenceGame;
